import { useState, useEffect, useRef } from "react";
import { useParams } from "wouter";
import { getApiUrl } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { useLanguage } from "@/components/language-provider";
import { useToast } from "@/hooks/use-toast";
import { FileText, Check, Loader2, Edit3, Move, PenTool, Type, Shield, Lock, Sparkles, AlertTriangle, ZoomIn, ZoomOut, XCircle, Copy, FileSignature, Crown, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { PdfCanvas } from "@/components/PdfCanvas";

type SignatureMethod = "draw" | "govbr" | "typed" | "upload";

interface SignaturePolicy {
  country: "brazil" | "international";
  allowedSignatureMethods: string;
  defaultSignatureMethod: SignatureMethod;
  requireCpf: boolean;
}

export default function Sign() {
  const params = useParams<{ token: string }>();
  const { t, language, setLanguage } = useLanguage();
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<{ x: number; y: number; pressure: number }[][]>([]);
  
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureRequest, setSignatureRequest] = useState<any>(null);
  const [document, setDocument] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(true);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showPositionPicker, setShowPositionPicker] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signedDocUrl, setSignedDocUrl] = useState<string | null>(null);
  
  const [pageCount, setPageCount] = useState(1);
  const [manualPosition, setManualPosition] = useState({ x: 72, y: 100, page: 0 });
  const [useManualPosition, setUseManualPosition] = useState(false);
  
  const [signaturePolicy, setSignaturePolicy] = useState<SignaturePolicy>({
    country: "international",
    allowedSignatureMethods: "draw,govbr,typed",
    defaultSignatureMethod: "draw",
    requireCpf: false,
  });
  
  const [selectedMethod, setSelectedMethod] = useState<SignatureMethod>("draw");
  const [typedName, setTypedName] = useState("");
  const [cpf, setCpf] = useState("");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [pendingSignatureData, setPendingSignatureData] = useState<string | null>(null);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);
  const signatureUploadRef = useRef<HTMLInputElement>(null);
  const [showPositionConfirm, setShowPositionConfirm] = useState(false);
  const [showAutoPlaceAnimation, setShowAutoPlaceAnimation] = useState(false);
  const [autoPlaceProgress, setAutoPlaceProgress] = useState(0);
  const [positionWarning, setPositionWarning] = useState<string | null>(null);
  const [hasSenderPosition, setHasSenderPosition] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [formFields, setFormFields] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [pdfPage, setPdfPage] = useState(0);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [applyToAllPages, setApplyToAllPages] = useState(false);
  const [alsoAddMainSignature, setAlsoAddMainSignature] = useState(false);
  
  // Free plan AI positioning popup
  const [showFreePlanInfo, setShowFreePlanInfo] = useState(false);
  const [senderPlan, setSenderPlan] = useState<string>("free");
  const [senderSignatureCount, setSenderSignatureCount] = useState(0);
  const [hasAiPositioning, setHasAiPositioning] = useState(true);

  useEffect(() => {
    loadSignatureRequest();
  }, [params.token]);

  const loadSignatureRequest = async () => {
    try {
      const res = await fetch(getApiUrl(`/api/sign/${params.token}`));
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || errorData.errors?.[0] || "Invalid or expired link");
      }
      
      const rawData = await res.json();
      // Handle both direct response and data-wrapped response
      const data = rawData.data || rawData;
      
      console.log("Sign page data loaded:", data); // Debug logging

      // Signed on the gov.br portal: the step-by-step and the upload live on their own page.
      if (data.request?.isGovBrFlow) {
        window.location.replace(`/upload-signed/${params.token}`);
        return;
      }
      
      setSignatureRequest(data.request);
      setDocument(data.document);
      setPageCount(data.pageCount || 1);
      
      // Use sender-defined position if available, otherwise default to last page
      if (data.signaturePosition?.hasSenderPosition) {
        setManualPosition({ 
          x: data.signaturePosition.x, 
          y: data.signaturePosition.y, 
          page: data.signaturePosition.page 
        });
        setHasSenderPosition(true);
        setPositionWarning(null);
        // Skip AI — sender already defined position
        setHasAiPositioning(false);
      } else {
        setHasSenderPosition(false);
        setManualPosition({ x: 72, y: 100, page: Math.max(0, (data.pageCount || 1) - 1) });
      }
      
      // Get sender plan info
      if (data.senderPlan) {
        setSenderPlan(data.senderPlan);
      }
      if (typeof data.senderSignatureCount === "number") {
        setSenderSignatureCount(data.senderSignatureCount);
      }

      // Load form fields that signer must fill
      if (data.formFields && Array.isArray(data.formFields) && data.formFields.length > 0) {
        setFormFields(data.formFields);
        // Initialize field values with empty strings
        const initial: Record<string, string> = {};
        data.formFields.forEach((f: any) => { initial[f.id] = ""; });
        setFieldValues(initial);
      }
      
      // Check if AI positioning is available
      // Free plan: only first signature has AI positioning
      const isFree = !data.senderPlan || data.senderPlan === "free";
      const signatureNum = data.senderSignatureCount || 0;
      const aiAvailable = !isFree || signatureNum === 0;
      setHasAiPositioning(aiAvailable);
      
      // Previously the app displayed an AI positioning info popup to signers on the 2nd and 3rd
      // signature of the free plan. Since signers no longer position signatures (only senders do),
      // we no longer show this popup on the signing page. The logic remains commented out.
      // if (isFree && signatureNum > 0 && signatureNum < 3) {
      //   setShowFreePlanInfo(true);
      // }
      
      if (data.signaturePolicy) {
        setSignaturePolicy(data.signaturePolicy);
        const methods = data.signaturePolicy.allowedSignatureMethods.split(",");
        if (methods.length === 1) {
          setSelectedMethod(methods[0] as SignatureMethod);
        } else {
          setSelectedMethod(data.signaturePolicy.defaultSignatureMethod || "draw");
        }
        // Auto-set language based on sender's company country
        if (data.signaturePolicy.country === "brazil") {
          setLanguage("pt");
        } else {
          setLanguage("en");
        }
      }
      
      if (data.request?.recipientName) {
        setTypedName(data.request.recipientName);
      }
      
      // Check if already signed - pending and viewed are valid states for signing
      const status = data.request?.status?.toLowerCase();
      console.log("Recipient status:", status); // Debug logging
      
      if (status === "signed" || status === "declined" || status === "completed") {
        setSigned(true);
      } else {
        setShowPrompt(true);
      }
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getAllowedMethods = () => signaturePolicy.allowedSignatureMethods.split(",") as SignatureMethod[];

  const handleSignNow = async () => {
    setShowPrompt(false);
    // Position is already defined by sender - go directly to signature pad
    // No AI positioning, no manual positioning needed
    setShowSignaturePad(true);
  };

  const handleReviewFirst = () => {
    setShowPrompt(false);
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      const res = await fetch(getApiUrl(`/api/sign/${params.token}/decline`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: declineReason || null }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to decline");
      }
      
      setShowDeclineDialog(false);
      setDeclined(true);
      toast({ title: t("sign.declinedTitle") || (language === "pt" ? "Recusado" : "Declined"), description: t("sign.declinedDesc") || (language === "pt" ? "Você recusou assinar este documento" : "You have declined to sign this document") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setDeclining(false);
    }
  };

  const handleManualPosition = () => {
    setShowPrompt(false);
    setShowPositionPicker(true);
    setUseManualPosition(true);
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    let drawing = false;
    let strokePoints: { x: number; y: number; pressure: number }[] = [];
    let allStrokes: { x: number; y: number; pressure: number }[][] = [];
    
    const getPos = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      let pressure = 0.5;
      
      if ("touches" in e && e.touches[0]) {
        const touch = e.touches[0] as any;
        pressure = touch.force || 0.5;
        return { 
          x: (e.touches[0].clientX - rect.left) * scaleX, 
          y: (e.touches[0].clientY - rect.top) * scaleY,
          pressure
        };
      }
      return { 
        x: ((e as MouseEvent).clientX - rect.left) * scaleX, 
        y: ((e as MouseEvent).clientY - rect.top) * scaleY,
        pressure
      };
    };
    
    const catmullRomSpline = (p0: {x: number, y: number}, p1: {x: number, y: number}, 
                              p2: {x: number, y: number}, p3: {x: number, y: number}, t: number) => {
      const t2 = t * t;
      const t3 = t2 * t;
      return {
        x: 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + 
            (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + 
            (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + 
            (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + 
            (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3)
      };
    };
    
    const redrawCanvas = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (const stroke of allStrokes) {
        if (stroke.length < 2) continue;
        drawSmoothStroke(stroke);
      }
      
      if (strokePoints.length >= 2) {
        drawSmoothStroke(strokePoints);
      }
    };
    
    const drawSmoothStroke = (points: { x: number; y: number; pressure: number }[]) => {
      if (points.length < 2) return;
      
      ctx.strokeStyle = "#000000";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      
      if (points.length === 2) {
        ctx.lineWidth = 2 + points[0].pressure;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        ctx.lineTo(points[1].x, points[1].y);
        ctx.stroke();
        return;
      }
      
      const smoothPoints: { x: number; y: number; pressure: number }[] = [];
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[Math.min(points.length - 1, i + 1)];
        const p3 = points[Math.min(points.length - 1, i + 2)];
        
        const segments = 4;
        for (let t = 0; t < segments; t++) {
          const point = catmullRomSpline(p0, p1, p2, p3, t / segments);
          const pressure = p1.pressure + (p2.pressure - p1.pressure) * (t / segments);
          smoothPoints.push({ ...point, pressure });
        }
      }
      smoothPoints.push(points[points.length - 1]);
      
      for (let i = 0; i < smoothPoints.length - 1; i++) {
        const p1 = smoothPoints[i];
        const p2 = smoothPoints[i + 1];
        const lineWidth = 1.5 + p1.pressure * 1.5;
        
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
    };
    
    const startDraw = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      drawing = true;
      const pos = getPos(e);
      strokePoints = [pos];
    };
    
    const draw = (e: MouseEvent | TouchEvent) => {
      if (!drawing) return;
      e.preventDefault();
      
      const pos = getPos(e);
      const lastPoint = strokePoints[strokePoints.length - 1];
      const dist = Math.sqrt(Math.pow(pos.x - lastPoint.x, 2) + Math.pow(pos.y - lastPoint.y, 2));
      
      if (dist > 1.5) {
        strokePoints.push(pos);
        redrawCanvas();
      }
    };
    
    const endDraw = (e: MouseEvent | TouchEvent) => {
      if (!drawing) return;
      drawing = false;
      
      if (strokePoints.length >= 2) {
        allStrokes.push([...strokePoints]);
        strokesRef.current = [...allStrokes];
      }
      strokePoints = [];
    };
    
    canvas.addEventListener("mousedown", startDraw);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", endDraw);
    canvas.addEventListener("mouseleave", endDraw);
    canvas.addEventListener("touchstart", startDraw, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", endDraw);
    
    (canvas as any)._clearStrokes = () => {
      allStrokes = [];
      strokePoints = [];
      strokesRef.current = [];
    };
    
    return () => {
      canvas.removeEventListener("mousedown", startDraw);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", endDraw);
      canvas.removeEventListener("mouseleave", endDraw);
      canvas.removeEventListener("touchstart", startDraw);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", endDraw);
    };
  };

  useEffect(() => {
    if (showSignaturePad && selectedMethod === "draw") {
      setTimeout(initCanvas, 100);
    }
  }, [showSignaturePad, selectedMethod]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if ((canvas as any)._clearStrokes) {
      (canvas as any)._clearStrokes();
    }
  };

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const validateCpf = (cpf: string) => {
    const digits = cpf.replace(/\D/g, "");
    return digits.length === 11;
  };

  const maskCpf = (cpf: string) => {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length !== 11) return cpf;
    return `***.${digits.slice(3, 6)}.${digits.slice(6, 9)}-**`;
  };

  const generateGovBrSignature = (name: string, cpfVal: string) => {
    const canvas = window.document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 120;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = "#1e3a5f";
    ctx.font = "bold 16px Arial";
    ctx.fillText(language === "pt" ? "Assinatura Eletrônica" : "Electronic Signature", 15, 25);
    
    ctx.fillStyle = "#334155";
    ctx.font = "14px Arial";
    ctx.fillText(name, 15, 50);
    
    if (cpfVal) {
      ctx.fillStyle = "#64748b";
      ctx.font = "12px Arial";
      ctx.fillText(`CPF: ${maskCpf(cpfVal)}`, 15, 72);
    }
    
    ctx.fillStyle = "#059669";
    ctx.font = "bold 11px Arial";
    ctx.fillText(language === "pt" ? "Documento assinado eletronicamente" : "Electronically signed document", 15, 100);
    
    ctx.fillStyle = "#94a3b8";
    ctx.font = "10px Arial";
    const timestamp = new Date().toLocaleString(language === "pt" ? "pt-BR" : "en-US");
    ctx.fillText(timestamp, 15, 114);
    
    return canvas.toDataURL("image/png");
  };

  const generateTypedSignature = (name: string) => {
    // GOV.BR-style typed signature: script name on top, then full name, date/time and the
    // "electronically signed" line in small type. 2x canvas so it stays sharp in the PDF.
    const canvas = window.document.createElement("canvas");
    const scale = 2;
    canvas.width = 400 * scale;
    canvas.height = 110 * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, 400, 110);
    const clean = name.trim();
    const shortName = clean.split(/\s+/).length > 3 ? `${clean.split(/\s+/)[0]} ${clean.split(/\s+/).slice(-1)[0]}` : clean;

    ctx.fillStyle = "#1a1a1a";
    ctx.textBaseline = "alphabetic";
    let size = 40;
    ctx.font = `${size}px 'Rancho', 'Dancing Script', 'Brush Script MT', cursive`;
    while (ctx.measureText(shortName).width > 360 && size > 22) { size -= 2; ctx.font = `${size}px 'Rancho', 'Dancing Script', 'Brush Script MT', cursive`; }
    ctx.fillText(shortName, 20, 52);

    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(20, 60); ctx.lineTo(380, 60); ctx.stroke();

    ctx.fillStyle = "#1a1a1a";
    ctx.font = "600 12px 'Geist', Arial, sans-serif";
    ctx.fillText(clean, 20, 76);
    ctx.fillStyle = "#6b7280";
    ctx.font = "10px 'Geist', Arial, sans-serif";
    const when = new Date().toLocaleString(language === "pt" ? "pt-BR" : "en-US", { dateStyle: "short", timeStyle: "short" });
    ctx.fillText(`${language === "pt" ? "Assinado eletronicamente em" : "Electronically signed on"} ${when}`, 20, 91);
    ctx.fillText(language === "pt" ? "FillSign · MP 2.200-2/2001 · Lei 14.063/2020" : "FillSign · Brazilian e-signature law", 20, 104);
    return canvas.toDataURL("image/png");
  };

  const handleApplySignature = async () => {
    // Validate required form fields first
    if (formFields.length > 0) {
      const missingFields = formFields.filter(f => f.required && !fieldValues[f.id]?.trim());
      if (missingFields.length > 0) {
        toast({ 
          title: t("sign.fillRequired") || (language === "pt" ? "Campos obrigatórios" : "Required fields"), 
          description: `${missingFields.map(f => f.label).join(", ")}`, 
          variant: "destructive" 
        });
        return;
      }
    }

    let sigData: string | null = null;
    
    if (selectedMethod === "draw") {
      const canvas = canvasRef.current;
      if (!canvas) return;
      sigData = canvas.toDataURL("image/png");
    } else if (selectedMethod === "govbr") {
      if (!typedName.trim()) {
        toast({ title: language === "pt" ? "Erro" : "Error", description: language === "pt" ? "Por favor insira seu nome" : "Please enter your name", variant: "destructive" });
        return;
      }
      if (signaturePolicy.requireCpf && !validateCpf(cpf)) {
        toast({ title: language === "pt" ? "Erro" : "Error", description: language === "pt" ? "Por favor insira um CPF válido (11 dígitos)" : "Please enter a valid CPF (11 digits)", variant: "destructive" });
        return;
      }
      sigData = generateGovBrSignature(typedName, cpf);
    } else if (selectedMethod === "typed") {
      if (!typedName.trim()) {
        toast({ title: language === "pt" ? "Erro" : "Error", description: language === "pt" ? "Por favor insira seu nome" : "Please enter your name", variant: "destructive" });
        return;
      }
      sigData = generateTypedSignature(typedName);
    } else if (selectedMethod === "upload") {
      if (!uploadedSignature) {
        toast({ title: t("common.error"), description: t("sign.pleaseUpload") || "Please upload a signature image", variant: "destructive" });
        return;
      }
      sigData = uploadedSignature;
    }
    
    if (!sigData) {
      toast({ title: language === "pt" ? "Erro" : "Error", description: language === "pt" ? "Falha ao gerar assinatura" : "Failed to generate signature", variant: "destructive" });
      return;
    }
    
    // Position is defined by sender, not by signer
    // Go directly to signing
    setPendingSignatureData(sigData);
    setShowSignaturePad(false);
    
    // Sign immediately - no position confirmation needed
    await submitSignature(sigData);
  };
  
  const submitSignature = async (sigData: string) => {
    setSigning(true);
    try {
      const res = await fetch(getApiUrl(`/api/sign/${params.token}/complete`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signatureData: sigData,
          positionX: 0,
          positionY: 0,
          pageNumber: 0,
          signatureMethod: selectedMethod,
          signerCpf: selectedMethod === "govbr" && cpf ? cpf.replace(/\D/g, "") : null,
          signerTypedName: selectedMethod !== "draw" ? typedName : null,
          applyToAllPages: false,
          alsoAddMainSignature: false,
          language: language,
          formFieldValues: formFields.length > 0
            ? formFields.map(f => ({ fieldId: f.id, value: fieldValues[f.id] || "", d365Field: f.d365Field || "" }))
            : null,
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to sign");
      }
      
      const result = await res.json();
      // Handle both direct response and data-wrapped response
      const responseData = result.data || result;
      setSignedDocUrl(responseData.signedDocumentUrl);
      setSigned(true);
      setPendingSignatureData(null);
      toast({ title: t("sign.success") || (language === "pt" ? "Documento assinado com sucesso!" : "Document signed successfully!") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
      setShowSignaturePad(true); // Go back to signature pad on error
    } finally {
      setSigning(false);
    }
  };

  // confirmManualPosition and cancelPositionConfirm are no longer needed
  // Position is defined by sender, not signer

  const handleEnhanceSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const strokes = strokesRef.current;
    if (strokes.length === 0) {
      toast({ title: language === "pt" ? "Sem assinatura" : "No signature", description: language === "pt" ? "Por favor, desenhe sua assinatura primeiro." : "Please draw your signature first.", variant: "destructive" });
      return;
    }
    
    setIsEnhancing(true);
    try {
      const enhancedStrokes = enhanceStrokesAlgorithmically(strokes);
      strokesRef.current = enhancedStrokes;
      redrawEnhancedStrokes(enhancedStrokes);
      showEnhanceAnimation();
      toast({ 
        title: t("ui.signature_enhanced"), 
        description: t("ui.your_signature_has_been_smoothed_while_preservin") 
      });
    } catch (error) {
      console.error("Enhancement error:", error);
      toast({ title: language === "pt" ? "Erro" : "Error", description: language === "pt" ? "Não foi possível aprimorar a assinatura." : "Could not enhance signature.", variant: "destructive" });
    } finally {
      setIsEnhancing(false);
    }
  };
  
  const enhanceStrokesAlgorithmically = (strokes: { x: number; y: number; pressure: number }[][]) => {
    return strokes.map(stroke => {
      if (stroke.length < 3) return stroke;
      
      let enhanced = resampleStroke(stroke, 3);
      enhanced = smoothStrokeMultiPass(enhanced, 3);
      enhanced = normalizePressure(enhanced);
      
      return enhanced;
    });
  };
  
  const resampleStroke = (stroke: { x: number; y: number; pressure: number }[], minDist: number) => {
    if (stroke.length < 2) return stroke;
    
    const resampled: { x: number; y: number; pressure: number }[] = [stroke[0]];
    let accumulated = 0;
    
    for (let i = 1; i < stroke.length; i++) {
      const prev = stroke[i - 1];
      const curr = stroke[i];
      const dist = Math.sqrt(Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2));
      accumulated += dist;
      
      if (accumulated >= minDist) {
        resampled.push(curr);
        accumulated = 0;
      }
    }
    
    if (resampled[resampled.length - 1] !== stroke[stroke.length - 1]) {
      resampled.push(stroke[stroke.length - 1]);
    }
    
    return resampled;
  };
  
  const smoothStrokeMultiPass = (stroke: { x: number; y: number; pressure: number }[], passes: number) => {
    let result = stroke;
    
    for (let p = 0; p < passes; p++) {
      const smoothed: { x: number; y: number; pressure: number }[] = [];
      
      for (let i = 0; i < result.length; i++) {
        if (i === 0 || i === result.length - 1) {
          smoothed.push(result[i]);
          continue;
        }
        
        const prev = result[i - 1];
        const curr = result[i];
        const next = result[i + 1];
        
        const tension = 0.25;
        smoothed.push({
          x: curr.x * (1 - tension * 2) + prev.x * tension + next.x * tension,
          y: curr.y * (1 - tension * 2) + prev.y * tension + next.y * tension,
          pressure: curr.pressure * (1 - tension * 2) + prev.pressure * tension + next.pressure * tension,
        });
      }
      
      result = smoothed;
    }
    
    return result;
  };
  
  const normalizePressure = (stroke: { x: number; y: number; pressure: number }[]) => {
    if (stroke.length === 0) return stroke;
    
    const pressures = stroke.map(p => p.pressure);
    const avgPressure = pressures.reduce((a, b) => a + b, 0) / pressures.length;
    const targetPressure = Math.max(0.4, Math.min(0.7, avgPressure));
    
    return stroke.map((point, i) => {
      const blendFactor = 0.6;
      const normalizedPressure = point.pressure * (1 - blendFactor) + targetPressure * blendFactor;
      return { ...point, pressure: normalizedPressure };
    });
  };
  
  const redrawEnhancedStrokes = (strokes: { x: number; y: number; pressure: number }[][]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.strokeStyle = "#000000";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    
    const catmullRom = (p0: {x: number, y: number}, p1: {x: number, y: number}, 
                        p2: {x: number, y: number}, p3: {x: number, y: number}, t: number) => {
      const t2 = t * t;
      const t3 = t2 * t;
      return {
        x: 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + 
            (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + 
            (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + 
            (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + 
            (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3)
      };
    };
    
    for (const stroke of strokes) {
      if (stroke.length < 2) continue;
      
      if (stroke.length === 2) {
        ctx.lineWidth = 2 + stroke[0].pressure;
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        ctx.lineTo(stroke[1].x, stroke[1].y);
        ctx.stroke();
        continue;
      }
      
      const smoothPoints: { x: number; y: number; pressure: number }[] = [];
      for (let i = 0; i < stroke.length - 1; i++) {
        const p0 = stroke[Math.max(0, i - 1)];
        const p1 = stroke[i];
        const p2 = stroke[Math.min(stroke.length - 1, i + 1)];
        const p3 = stroke[Math.min(stroke.length - 1, i + 2)];
        
        const segments = 6;
        for (let t = 0; t < segments; t++) {
          const point = catmullRom(p0, p1, p2, p3, t / segments);
          const pressure = p1.pressure + (p2.pressure - p1.pressure) * (t / segments);
          smoothPoints.push({ ...point, pressure });
        }
      }
      smoothPoints.push(stroke[stroke.length - 1]);
      
      for (let i = 0; i < smoothPoints.length - 1; i++) {
        const p1 = smoothPoints[i];
        const p2 = smoothPoints[i + 1];
        const lineWidth = 1.5 + p1.pressure * 1.5;
        
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
    }
  };

  const renderSvgPathsToCanvas = (svgPaths: { d: string; strokeWidth?: number }[], viewBox: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const [, , vbWidth, vbHeight] = (viewBox || "0 0 400 150").split(" ").map(Number);
    const scaleX = canvas.width / vbWidth;
    const scaleY = canvas.height / vbHeight;
    const scale = Math.min(scaleX, scaleY);
    
    ctx.save();
    ctx.translate((canvas.width - vbWidth * scale) / 2, (canvas.height - vbHeight * scale) / 2);
    ctx.scale(scale, scale);
    
    ctx.strokeStyle = "#000000";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    
    for (const pathData of svgPaths) {
      ctx.lineWidth = pathData.strokeWidth || 2;
      const path = new Path2D(pathData.d);
      ctx.stroke(path);
    }
    
    ctx.restore();
    
    if ((canvas as any)._clearStrokes) {
      (canvas as any)._clearStrokes();
    }
  };

  const showEnhanceAnimation = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.style.transition = "box-shadow 0.5s, transform 0.2s";
    canvas.style.boxShadow = "0 0 25px rgba(34, 197, 94, 0.6)";
    canvas.style.transform = "scale(1.02)";
    setTimeout(() => {
      canvas.style.boxShadow = "";
      canvas.style.transform = "";
    }, 600);
  };

  const applyLocalEnhancement = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    const width = canvas.width;
    const height = canvas.height;
    
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const gray = (r + g + b) / 3;
      
      if (gray < 180) {
        data[i] = Math.max(0, r - 40);
        data[i + 1] = Math.max(0, g - 40);
        data[i + 2] = Math.max(0, b - 40);
      } else if (gray > 230) {
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    showEnhanceAnimation();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!signatureRequest || !document) {
    return (
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-8 sm:py-12">
            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-xl font-semibold mb-2">{t("ui.link_not_found")}</h2>
            <p className="text-muted-foreground">{t("ui.this_signature_link_is_invalid_or_has_expired")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (declined || signatureRequest.status === "declined") {
    return (
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-6 bg-muted/30">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-8 sm:py-12">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold mb-2">{t("sign.declinedTitle") || (language === "pt" ? "Assinatura Recusada" : "Signature Declined")}</h2>
            <p className="text-muted-foreground mb-6">
              {t("sign.declinedMessage") || (language === "pt" ? "Você recusou assinar este documento. O remetente foi notificado." : "You have declined to sign this document. The sender has been notified.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-6 bg-muted/30">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-8 sm:py-12">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold mb-2">{t("sign.success")}</h2>
            <p className="text-muted-foreground mb-6">
              {(t("sign.successMessage") || "Thank you, {name}. The document has been signed successfully.").replace("{name}", signatureRequest?.recipientName || "")}
            </p>
            
            {/* Download original document button */}
            {document?.fileUrl && (
              <a href={document.fileUrl} target="_blank" rel="noopener noreferrer" className="block mb-4">
                <Button variant="outline" className="w-full">
                  <FileText className="mr-2 h-4 w-4" /> 
                  {t("sign.downloadOriginal") || (language === "pt" ? "Baixar Documento Original" : "Download Original Document")}
                </Button>
              </a>
            )}
            
            {/* Info message about receiving final document */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-left">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>{t("sign.pendingSignatures") || "Pending Signatures"}</strong>
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
                {t("sign.pendingSignaturesMessage") || "When all signers have signed, you will receive an email with the final signed document attached."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allowedMethods = getAllowedMethods();
  const showMethodTabs = allowedMethods.length > 1;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b bg-background flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4">
        <BrandLogo size="md" />
        <div className="flex items-center gap-1 sm:gap-2">
          <LanguageToggle />
          <ThemeToggle />
          {signatureRequest.signatureMethod !== "govbr" && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowDeclineDialog(true)} 
              data-testid="button-decline-header"
            >
              <XCircle className="h-4 w-4" /> <span className="hidden sm:inline ml-1">{t("sign.decline") || "Decline"}</span>
            </Button>
          )}
          <Button size="sm" onClick={handleSignNow} data-testid="button-sign-header">
            <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline ml-1">{t("sign.signNow")}</span>
          </Button>
        </div>
      </header>

      <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("sign.title")}</DialogTitle>
            <DialogDescription>
              {language === "pt" 
                ? `Olá ${signatureRequest.recipientName}, você foi solicitado(a) a assinar "${document.name}".`
                : `Hello ${signatureRequest.recipientName}, you've been asked to sign "${document.name}".`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-6">
              {language === "pt" ? "Escolha como deseja prosseguir:" : "Choose how you'd like to proceed:"}
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={handleSignNow} data-testid="button-sign-now">
                <Sparkles className="mr-2 h-4 w-4" /> {t("sign.signNow")}
              </Button>
              <Button variant="ghost" onClick={handleReviewFirst} data-testid="button-review-first">
                <FileText className="mr-2 h-4 w-4" /> {t("sign.reviewFirst")}
              </Button>
              {signatureRequest.signatureMethod !== "govbr" && (
                <Button 
                  variant="ghost" 
                  className="text-destructive hover:text-destructive" 
                  onClick={() => setShowDeclineDialog(true)} 
                  data-testid="button-decline"
                >
                  <XCircle className="mr-2 h-4 w-4" /> {t("sign.decline") || "Decline to Sign"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("sign.declineTitle") || (language === "pt" ? "Recusar Assinatura" : "Decline to Sign")}</DialogTitle>
            <DialogDescription>
              {t("sign.declineDesc") || (language === "pt" ? "Tem certeza que deseja recusar a assinatura deste documento? O remetente será notificado." : "Are you sure you want to decline signing this document? The sender will be notified.")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="decline-reason">{t("sign.declineReasonLabel") || (language === "pt" ? "Motivo (opcional)" : "Reason (optional)")}</Label>
              <Textarea
                id="decline-reason"
                placeholder={t("sign.declineReasonPlaceholder") || "Explain why you're declining..."}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="textarea-decline-reason"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Button 
                variant="destructive" 
                onClick={handleDecline} 
                disabled={declining}
                data-testid="button-confirm-decline"
              >
                {declining ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                {t("sign.confirmDecline") || (language === "pt" ? "Confirmar Recusa" : "Confirm Decline")}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowDeclineDialog(false)}
                data-testid="button-cancel-decline"
              >
                {t("sign.cancelDecline") || (language === "pt" ? "Cancelar" : "Cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Removed the free plan AI positioning info popup. Senders position signatures through the
          document wizard, so this popup is no longer shown to recipients. */}

      <Dialog open={showAutoPlaceAnimation} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <div className="py-8 text-center">
            <div className="relative mx-auto w-20 h-20 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
              <div 
                className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"
                style={{ animationDuration: "1s" }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
            </div>
            
            <h3 className="text-lg font-semibold mb-2">{t("ui.finding_the_best_position")}</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {t("sign.aiSearching").replace("{name}", signatureRequest.recipientName || "")}
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${autoPlaceProgress >= 30 ? "bg-green-500" : "bg-muted"}`}>
                  {autoPlaceProgress >= 30 && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className={autoPlaceProgress >= 30 ? "text-foreground" : "text-muted-foreground"}>
                  {t("ui.scanning_document_content")}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${autoPlaceProgress >= 60 ? "bg-green-500" : "bg-muted"}`}>
                  {autoPlaceProgress >= 60 && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className={autoPlaceProgress >= 60 ? "text-foreground" : "text-muted-foreground"}>
                  {t("sign.searchingFor").replace("{name}", signatureRequest.recipientName || "")}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${autoPlaceProgress >= 90 ? "bg-green-500" : "bg-muted"}`}>
                  {autoPlaceProgress >= 90 && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className={autoPlaceProgress >= 90 ? "text-foreground" : "text-muted-foreground"}>
                  {t("ui.determining_optimal_position")}
                </span>
              </div>
            </div>
            
            <div className="mt-6">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300 ease-out"
                  style={{ width: `${autoPlaceProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">{autoPlaceProgress}% complete</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSignaturePad} onOpenChange={setShowSignaturePad}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("sign.signDocument") || (language === "pt" ? "Assinar Documento" : "Sign Document")}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {showMethodTabs 
                ? (t("sign.chooseMethod") || (language === "pt" ? "Escolha o método de assinatura" : "Choose your preferred signature method"))
                : (language === "pt" 
                    ? `Assinar usando ${selectedMethod === "draw" ? "assinatura desenhada" : selectedMethod === "govbr" ? "nome e CPF" : "nome digitado"}`
                    : `Sign using ${selectedMethod === "draw" ? "hand-drawn signature" : selectedMethod === "govbr" ? "name and ID" : "typed name"}`)
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 sm:py-4">
            {/* Form fields that signer must fill */}
            {formFields.length > 0 && (
              <div className="mb-4 space-y-3 p-3 bg-muted/50 rounded-lg border">
                <p className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  {t("sign.fillFields") || (language === "pt" ? "Preencha as informações obrigatórias" : "Please fill in the required information")}
                </p>
                {formFields.map((field: any) => (
                  <div key={field.id} className="space-y-1">
                    <Label className="text-xs sm:text-sm">
                      {field.label} {field.required && <span className="text-destructive">*</span>}
                    </Label>
                    {field.type === "dropdown" && field.options ? (
                      <select
                        className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                        value={fieldValues[field.id] || ""}
                        onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        data-testid={`field-${field.id}`}
                      >
                        <option value="">{field.placeholder || t("sign.selectPlaceholder")}</option>
                        {field.options.map((opt: string) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === "checkbox" ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={fieldValues[field.id] === "true"}
                          onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.checked ? "true" : "" }))}
                          className="h-4 w-4 rounded border"
                          data-testid={`field-${field.id}`}
                        />
                        <span className="text-sm text-muted-foreground">{field.placeholder}</span>
                      </div>
                    ) : (
                      <Input
                        type={field.type === "date" ? "date" : field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
                        placeholder={field.placeholder || field.label}
                        value={fieldValues[field.id] || ""}
                        onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid={`field-${field.id}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {showMethodTabs ? (
              <Tabs value={selectedMethod} onValueChange={(v) => setSelectedMethod(v as SignatureMethod)}>
                <TabsList className={`grid w-full mb-4 ${
                  allowedMethods.length === 4 ? "grid-cols-4" : 
                  allowedMethods.length === 3 ? "grid-cols-3" : 
                  allowedMethods.length === 2 ? "grid-cols-2" : "grid-cols-1"
                }`}>
                  {allowedMethods.includes("draw") && (
                    <TabsTrigger value="draw" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2" data-testid="tab-draw">
                      <PenTool className="h-4 w-4" /> <span className="hidden sm:inline">{t("sign.draw") || "Draw"}</span>
                    </TabsTrigger>
                  )}
                  {allowedMethods.includes("typed") && (
                    <TabsTrigger value="typed" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2" data-testid="tab-typed">
                      <Type className="h-4 w-4" /> <span className="hidden sm:inline">{t("sign.type") || "Type"}</span>
                    </TabsTrigger>
                  )}
                  {/* Upload signature image */}
                  <TabsTrigger value="upload" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2" data-testid="tab-upload">
                    <FileSignature className="h-4 w-4" /> <span className="hidden sm:inline">{t("sign.upload") || "Upload"}</span>
                  </TabsTrigger>
                  {allowedMethods.includes("govbr") && (
                    <TabsTrigger value="govbr" className="flex items-center gap-1 text-xs sm:text-sm sm:gap-2" data-testid="tab-govbr">
                      <Shield className="h-4 w-4" /> <span className="hidden sm:inline">{language === "pt" ? "Nome+CPF" : "Name+ID"}</span>
                    </TabsTrigger>
                  )}
                </TabsList>

                {allowedMethods.includes("draw") && (
                  <TabsContent value="draw" className="space-y-4">
                    <div className="border rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="w-full cursor-crosshair"
                        style={{ touchAction: "none", background: "#ffffff" }}
                      />
                    </div>
                    <div className="flex justify-between gap-2">
                      <Button variant="outline" size="sm" onClick={clearSignature}>
                        {t("sign.clear")}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleEnhanceSignature} disabled={isEnhancing}>
                        {isEnhancing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                        {language === "pt" ? "Aprimorar com IA" : "Enhance with AI"}
                      </Button>
                    </div>
                  </TabsContent>
                )}

                {allowedMethods.includes("govbr") && (
                  <TabsContent value="govbr" className="space-y-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border space-y-4">
                      <div className="flex items-center gap-2 text-green-600">
                        <Shield className="h-5 w-5" />
                        <span className="font-medium">
                          {language === "pt" ? "Assinatura com Nome e CPF" : "Signature with Name and ID"}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">
                        {language === "pt" 
                          ? "Sua identidade será registrada com nome completo, CPF, endereço IP e data/hora. Esta assinatura tem validade jurídica nos termos da MP 2.200-2/2001."
                          : "Your identity will be recorded with full name, ID number, IP address and timestamp. This signature is legally binding under applicable electronic signature laws."}
                      </p>

                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="govbr-name">{language === "pt" ? "Nome completo" : "Full name"}</Label>
                          <Input
                            id="govbr-name"
                            value={typedName}
                            onChange={(e) => setTypedName(e.target.value)}
                            placeholder={language === "pt" ? "Digite seu nome completo" : "Enter your full name"}
                            data-testid="input-govbr-name"
                          />
                        </div>
                        {(signaturePolicy.requireCpf || signaturePolicy.country === "brazil") && (
                          <div className="space-y-2">
                            <Label htmlFor="cpf">CPF {signaturePolicy.requireCpf && <Badge variant="secondary" className="ml-2">{language === "pt" ? "Obrigatório" : "Required"}</Badge>}</Label>
                            <Input
                              id="cpf"
                              value={cpf}
                              onChange={(e) => setCpf(formatCpf(e.target.value))}
                              placeholder="000.000.000-00"
                              maxLength={14}
                              data-testid="input-cpf"
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="p-3 bg-white dark:bg-slate-800 rounded border">
                        <p className="text-xs text-muted-foreground mb-2">{language === "pt" ? "Prévia:" : "Preview:"}</p>
                        <div className="font-bold text-sm text-slate-700 dark:text-slate-300">
                          {language === "pt" ? "Assinatura Eletrônica" : "Electronic Signature"}
                        </div>
                        <div className="text-sm">{typedName || (language === "pt" ? "Seu nome" : "Your name")}</div>
                        {cpf && <div className="text-xs text-muted-foreground">CPF: {maskCpf(cpf)}</div>}
                        <div className="flex items-center gap-1 mt-2 text-green-600 text-xs font-medium">
                          <Lock className="h-3 w-3" />
                          {language === "pt" ? "Documento assinado eletronicamente" : "Electronically signed document"}
                        </div>
                      </div>
                      
                      <div className="text-[10px] text-muted-foreground bg-muted/50 rounded p-2 border">
                        {language === "pt" 
                          ? "Registros coletados: nome, CPF, endereço IP, navegador, data/hora UTC e hash SHA-256 do documento. Válido conforme Art. 10, §2º da MP 2.200-2/2001."
                          : "Collected evidence: name, ID, IP address, browser, UTC timestamp and SHA-256 document hash. Valid under ESIGN Act and eIDAS."}
                      </div>
                    </div>
                  </TabsContent>
                )}

                {allowedMethods.includes("typed") && (
                  <TabsContent value="typed" className="space-y-4">
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="typed-name">{t("ui.type_your_name_as_signature")}</Label>
                        <Input
                          id="typed-name"
                          value={typedName}
                          onChange={(e) => setTypedName(e.target.value)}
                          placeholder={t("ui.enter_your_full_name")}
                          className="text-lg"
                          data-testid="input-typed-name"
                        />
                      </div>
                      <div className="border rounded-lg p-4 bg-white min-h-[80px] flex items-center justify-center">
                        <span 
                          className="text-3xl italic text-slate-800"
                          style={{ fontFamily: "'Brush Script MT', cursive, sans-serif" }}
                        >
                          {typedName || t("sign.yourSignature")}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        {t("ui.your_typed_name_will_appear_as_a_stylized_signat")}
                      </p>
                    </div>
                  </TabsContent>
                )}

                {/* Upload Signature Image Tab */}
                <TabsContent value="upload" className="space-y-4">
                  <input
                    ref={signatureUploadRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          toast({
                            title: t("sign.fileTooLarge") || "File too large",
                            description: t("sign.maxSize") || "Maximum size is 2MB",
                            variant: "destructive"
                          });
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setUploadedSignature(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    data-testid="input-signature-upload"
                  />
                  <div className="space-y-4">
                    {!uploadedSignature ? (
                      <div 
                        onClick={() => signatureUploadRef.current?.click()}
                        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                      >
                        <FileSignature className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                        <p className="font-medium">{t("sign.clickToUpload") || "Click to upload signature image"}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t("sign.uploadFormats") || "PNG or JPG, max 2MB"}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="border rounded-lg p-4 bg-white">
                          <img 
                            src={uploadedSignature} 
                            alt={t("sign.uploadedSignatureAlt")} 
                            className="max-h-[120px] mx-auto object-contain"
                          />
                        </div>
                        <div className="flex justify-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setUploadedSignature(null);
                              if (signatureUploadRef.current) signatureUploadRef.current.value = "";
                            }}
                          >
                            {t("sign.removeImage") || "Remove"}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => signatureUploadRef.current?.click()}
                          >
                            {t("sign.changeImage") || "Change Image"}
                          </Button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground text-center">
                      {t("sign.uploadTip") || "For best results, use a signature on white background"}
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="space-y-4">
                {selectedMethod === "draw" && (
                  <>
                    <div className="border rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="w-full cursor-crosshair"
                        style={{ touchAction: "none", background: "#ffffff" }}
                      />
                    </div>
                    <div className="flex justify-between gap-2">
                      <Button variant="outline" size="sm" onClick={clearSignature}>
                        {t("sign.clear")}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleEnhanceSignature} disabled={isEnhancing}>
                        {isEnhancing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                        {language === "pt" ? "Aprimorar com IA" : "Enhance with AI"}
                      </Button>
                    </div>
                  </>
                )}
                
                {selectedMethod === "govbr" && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border space-y-4">
                    <div className="flex items-center gap-2 text-green-600">
                      <Shield className="h-5 w-5" />
                      <span className="font-medium">{language === "pt" ? "Assinatura com Nome e CPF" : "Signature with Name and ID"}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {language === "pt" 
                        ? "Sua identidade será registrada com nome completo, CPF, endereço IP e data/hora."
                        : "Your identity will be recorded with full name, ID number, IP address and timestamp."}
                    </p>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="govbr-name-single">{language === "pt" ? "Nome completo" : "Full Name"}</Label>
                        <Input
                          id="govbr-name-single"
                          value={typedName}
                          onChange={(e) => setTypedName(e.target.value)}
                          placeholder={language === "pt" ? "Digite seu nome completo" : "Enter your full name"}
                          data-testid="input-govbr-name-single"
                        />
                      </div>
                      {(signaturePolicy.requireCpf || signaturePolicy.country === "brazil") && (
                        <div className="space-y-2">
                          <Label htmlFor="cpf-single">CPF {signaturePolicy.requireCpf && <Badge variant="secondary" className="ml-2">{language === "pt" ? "Obrigatório" : "Required"}</Badge>}</Label>
                          <Input
                            id="cpf-single"
                            value={cpf}
                            onChange={(e) => setCpf(formatCpf(e.target.value))}
                            placeholder="000.000.000-00"
                            maxLength={14}
                            data-testid="input-cpf-single"
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-4 p-3 bg-white dark:bg-slate-800 rounded border">
                      <p className="text-xs text-muted-foreground mb-2">{language === "pt" ? "Prévia:" : "Preview:"}</p>
                      <div className="font-bold text-sm text-slate-700 dark:text-slate-300">{language === "pt" ? "Assinatura Eletrônica" : "Electronic Signature"}</div>
                      <div className="text-sm">{typedName || (language === "pt" ? "Seu nome" : "Your name")}</div>
                      {cpf && <div className="text-xs text-muted-foreground">CPF: {maskCpf(cpf)}</div>}
                      <div className="flex items-center gap-1 mt-2 text-green-600 text-xs font-medium">
                        <Lock className="h-3 w-3" />
                        {language === "pt" ? "Documento assinado eletronicamente" : "Electronically signed document"}
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedMethod === "typed" && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="typed-name-single">{t("ui.type_your_name_as_signature")}</Label>
                      <Input
                        id="typed-name-single"
                        value={typedName}
                        onChange={(e) => setTypedName(e.target.value)}
                        placeholder={t("ui.enter_your_full_name")}
                        className="text-lg"
                        data-testid="input-typed-name-single"
                      />
                    </div>
                    <div className="border rounded-lg p-4 bg-white min-h-[80px] flex items-center justify-center">
                      <span 
                        className="text-3xl italic text-slate-800"
                        style={{ fontFamily: "'Brush Script MT', cursive, sans-serif" }}
                      >
                        {typedName || t("sign.yourSignature")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      {t("ui.your_typed_name_will_appear_as_a_stylized_signat")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Legal declaration */}
            <div className="text-[10px] sm:text-xs text-muted-foreground bg-muted/50 rounded p-2 sm:p-3 mt-3 border">
              {signaturePolicy.country === "brazil" ? (
                <p>Ao clicar em &quot;Aplicar Assinatura&quot;, declaro que li e concordo com o conteúdo deste documento. 
                Esta assinatura eletrônica possui validade jurídica nos termos do Art. 10, §2º da MP 2.200-2/2001, 
                tendo as partes se utilizado de meio eletrônico para manifestação de vontade.</p>
              ) : (
                <p>By clicking &quot;Apply Signature&quot;, I declare that I have read and agree to the contents of this document. 
                This electronic signature is legally binding under applicable electronic signature laws, including 
                ESIGN Act, eIDAS, and UNCITRAL Model Law on Electronic Signatures.</p>
              )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-4 sm:mt-6">
              <Button variant="outline" onClick={() => setShowSignaturePad(false)} className="w-full sm:w-auto">
                {t("sign.cancel") || (language === "pt" ? "Cancelar" : "Cancel")}
              </Button>
              <Button onClick={handleApplySignature} disabled={signing} data-testid="button-apply-signature" className="w-full sm:w-auto">
                {signing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                {t("sign.apply")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signing in progress overlay */}
      {signing && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="p-6 text-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <div>
              <p className="font-semibold text-lg">{t("sign.signingInProgress") || (language === "pt" ? "Assinando documento..." : "Signing document...")}</p>
              <p className="text-sm text-muted-foreground">{t("sign.pleaseWait") || (language === "pt" ? "Por favor aguarde" : "Please wait")}</p>
            </div>
          </Card>
        </div>
      )}

      <main className="p-3 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-primary" />
                {document.name}
              </CardTitle>
              {signatureRequest.message && (
                <CardDescription>
                  {language === "pt" ? "Mensagem" : "Message"}: &quot;{signatureRequest.message}&quot;
                </CardDescription>
              )}
              {hasSenderPosition && (
                <div className="mt-2 flex items-center gap-2 text-sm bg-primary/10 text-primary rounded-md px-3 py-2">
                  <FileSignature className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {t("sign.positionDefined") || (language === "pt" ? `Posição definida pelo remetente — Página ${manualPosition.page + 1}` : `Signature position defined by sender — Page ${manualPosition.page + 1}`)}
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Zoom + page navigation */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setPreviewZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} disabled={previewZoom <= 0.5}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-xs font-medium min-w-[40px] text-center">{Math.round(previewZoom * 100)}%</span>
                <Button variant="outline" size="sm" onClick={() => setPreviewZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} disabled={previewZoom >= 3}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                {previewZoom !== 1 && (
                  <Button variant="ghost" size="sm" onClick={() => setPreviewZoom(1)} className="text-xs">{t("ui.reset")}</Button>
                )}
                {/* Page nav for PdfCanvas mode */}
                {pageCount > 1 && (
                  <>
                    <span className="border-l h-5 mx-1" />
                    <Button variant="outline" size="sm" disabled={pdfPage <= 0} onClick={() => setPdfPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs min-w-[50px] text-center">{pdfPage + 1}/{pageCount}</span>
                    <Button variant="outline" size="sm" disabled={pdfPage >= pageCount - 1} onClick={() => setPdfPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              <div className="bg-muted rounded-lg overflow-auto" style={{ maxHeight: "calc(100vh - 250px)" }}>
                {formFields.some(f => f.page !== undefined && f.x !== undefined) ? (
                  /* PdfCanvas with form field overlays (the overlay is sized to the rendered page, so zoom keeps fields in place) */
                  <div className="relative bg-white">
                    <PdfCanvas
                      src={document.fileUrl}
                      pageNumber={pdfPage}
                      onPageCount={(c) => setPageCount(c)}
                      zoom={previewZoom}
                    >
                      {/* Form fields overlay */}
                      <div className="absolute inset-0">
                        {formFields.filter(f => f.page === pdfPage).map((field: any) => (
                          <div
                            key={field.id}
                            className="absolute"
                            style={{
                              left: `${field.x ?? 10}%`,
                              top: `${field.y ?? 10}%`,
                              width: `${field.width ?? 30}%`,
                            }}
                          >
                            {field.type === "checkbox" ? (
                              <label className="flex items-center gap-1.5 bg-blue-50 border border-blue-300 rounded px-1.5 py-0.5 cursor-pointer hover:bg-blue-100 transition-colors">
                                <input
                                  type="checkbox"
                                  checked={fieldValues[field.id] === "true"}
                                  onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.checked ? "true" : "" }))}
                                  className="h-3.5 w-3.5"
                                />
                                <span className="text-[11px] text-blue-800 truncate">{field.label}{field.required ? " *" : ""}</span>
                              </label>
                            ) : field.type === "dropdown" && field.options ? (
                              <select
                                className="w-full h-7 text-[11px] bg-blue-50 border border-blue-300 rounded px-1.5 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                                value={fieldValues[field.id] || ""}
                                onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                title={field.label}
                              >
                                <option value="">{field.placeholder || field.label}{field.required ? " *" : ""}</option>
                                {field.options.map((opt: string) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={field.type === "date" ? "date" : field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
                                className="w-full h-7 text-[11px] bg-blue-50 border border-blue-300 rounded px-1.5 placeholder:text-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                                placeholder={`${field.placeholder || field.label}${field.required ? " *" : ""}`}
                                value={fieldValues[field.id] || ""}
                                onChange={(e) => setFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                title={field.label}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </PdfCanvas>
                  </div>
                ) : (
                  /* Cross-browser PDF rendering via pdf.js canvas (no positioned fields) */
                  <div className="relative bg-white">
                    <PdfCanvas
                      src={document.fileUrl}
                      pageNumber={pdfPage}
                      onPageCount={(c) => setPageCount(c)}
                      zoom={previewZoom}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Written commitment (batch 54): the signer's contact is used for this document only. */}
        <p className="mx-auto mt-6 max-w-3xl px-2 text-center text-xs text-muted-foreground" data-testid="no-marketing-note">
          <Lock className="mr-1 inline h-3 w-3 align-[-2px]" />
          {language === "pt"
            ? "A FillSign usa o seu e-mail e o seu telefone só para este documento: o convite, os lembretes e a cópia assinada. Nunca para propaganda, e a FillSign não vende os seus dados."
            : "FillSign uses your e-mail and phone only for this document: the invitation, the reminders and the signed copy. Never for advertising, and FillSign does not sell your data."}
        </p>
      </main>
    </div>
  );
}
